import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchSiteMap, patchSiteMapArea } from '@apis/siteMap';
import { isStaleRefusal } from '@apis/logisticsRevision';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { fold } from '../units';
import { useLogisticsEditLock } from '../revision/LogisticsEditLock';

/**
 * The stock form's "Area stored" (SLS site map) plumbing — extracted from
 * `StockEntryForm` so the form stays under the file-size gate and the area
 * rules live testably in one place.
 *
 * Same query key as the SiteMap panel, so TanStack dedupes rather than
 * fetching the map twice. The material's CURRENT area is derived from the
 * areas' own `materials` lists (fold-insensitive, the map's matching rule).
 */
export function useStockAreaAssignment(siteId: string, foldedMaterial: string) {
  const { t } = useTranslation();
  const { revision } = useLogisticsEditLock();
  const mapQuery = useQuery({
    queryKey: ['site-map', siteId],
    queryFn: ({ signal }) => fetchSiteMap(siteId, signal),
    enabled: Boolean(siteId),
  });
  const { floors } = useSiteLocations(siteId);
  const areas = mapQuery.data?.areas ?? [];
  const containingAreaId =
    areas.find((area) => area.materials.some((m) => fold(m) === foldedMaterial))?.areaId ?? '';

  /* An explicit pick remembers WHICH material it was made for: a choice made
     while the form showed material A must not silently apply to material B
     after the user re-picks the material (blank "Add stock" flow) — a stale
     choice would yank B out of its real area on save. A choice for a
     different material simply stops applying, and the derived containing
     area takes over again. */
  const [choice, setChoice] = useState<{ material: string; areaId: string } | null>(null);
  const chosenAreaId = choice && choice.material === foldedMaterial ? choice.areaId : containingAreaId;
  const choose = (areaId: string) => setChoice({ material: foldedMaterial, areaId });

  /* The locked line when the map offers nothing to pick: name the currently
     selected floor when a base plan is configured (its map simply has no
     areas), bare N/A when there is no plan at all. */
  const mapFloorName = mapQuery.data?.config
    ? (floors.find((f) => f.id === mapQuery.data.config?.floorId)?.name ?? mapQuery.data.config.floorId)
    : null;
  const naLabel = mapFloorName
    ? t('logistics.stockDialog.areaNA', { floor: mapFloorName })
    : t('logistics.stockDialog.areaNABare');

  /**
   * The area move, run AFTER the snapshot lands: leave the old area, join
   * the new one (one area per material — the AreaDrawer's rule). Never
   * throws: the snapshot is the record and has already been written, so a
   * map failure must not masquerade as "nothing saved" (which invites a
   * duplicate-snapshot retry) — the caller reports the partial outcome
   * distinctly off the returned boolean instead. A stale refusal is the
   * refresh dock's to announce, so it counts as nothing to report (`true`).
   */
  const commit = async (materialName: string): Promise<boolean> => {
    if (chosenAreaId === containingAreaId) return true;
    try {
      const previousArea = areas.find((area) => area.areaId === containingAreaId);
      if (previousArea) {
        await patchSiteMapArea(siteId, previousArea.areaId, {
          materials: previousArea.materials.filter((m) => fold(m) !== foldedMaterial),
        }, revision);
      }
      const nextArea = areas.find((area) => area.areaId === chosenAreaId);
      if (nextArea) {
        await patchSiteMapArea(siteId, nextArea.areaId, {
          materials: [...nextArea.materials, materialName],
        }, revision);
      }
      return true;
    } catch (error) {
      return isStaleRefusal(error);
    }
  };

  return { areas, chosenAreaId, choose, naLabel, commit };
}
